import { type FormEvent, useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { ClientLink } from "../lib/router-context";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch("/api/espace-client/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        setError("La demande n’a pas pu être envoyée. Veuillez réessayer.");
        return;
      }
      setSubmitted(true);
    } catch {
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold">Mot de passe oublié</h1>
          <p className="text-sm text-white/75 mt-2">Nous vous aiderons à retrouver l’accès à votre espace client.</p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl shadow-2xl p-6">
          {submitted ? (
            <div className="space-y-4 text-center" role="status">
              <div className="flex justify-center"><CheckCircle2 className="w-8 h-8 text-primary" /></div>
              <h2 className="font-semibold">Vérifiez votre boîte mail</h2>
              <p className="text-sm text-muted-foreground">
                Si un compte actif correspond à cette adresse, vous recevrez un lien pour définir votre mot de passe.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">Saisissez votre adresse email. Si elle correspond à un compte actif, un lien pour définir votre mot de passe vous sera envoyé.</p>
              <div>
                <label htmlFor="forgot-email" className="text-sm font-medium">Adresse email</label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  className="w-full h-11 mt-1.5 px-3 rounded-lg border border-input bg-background text-sm"
                  placeholder="votre@email.fr"
                />
              </div>
              {error && (
                <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full h-11 rounded-lg font-medium text-sm flex items-center justify-center gap-2 text-white disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #4e63f0 0%, #2facf3 100%)" }}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Envoyer le lien
              </button>
            </form>
          )}

          <div className="mt-5 text-center">
            <ClientLink to="/connexion" className="text-sm text-primary hover:underline">Retour à la connexion</ClientLink>
          </div>
        </div>
        <p className="text-center text-xs text-white/70">Espace Client Jobotto</p>
      </div>
    </div>
  );
}